<script lang="ts">
	import type {
		FlatAttachment,
		PartForClient as Part,
		SettingValues,
		UserSettings,
		CollectionMember,
		LoadedCollectionMember,
	} from 'utility/load-part'
	import type { SvelteAsr } from 'types/common'
	import type {
		BreakdownTemplates$result,
		LIB_partStoreData$result,
		PartScreenStageOne$result,
		CollectionMembershipLocalInventory$data,
	} from '$houdini'
	import type { PartModel } from 'utility/load-models-for-manufacturer'
	import type { CrudStore } from '@isoftdata/svelte-store-crud'
	import type { GetFieldEditabilityFn } from './part'
	import type { i18n } from 'types/common'

	import Table, {
		Td,
		TreeRow,
		treeify,
		type Column,
		type TreeNode,
		type IndexedRowProps,
	} from '@isoftdata/svelte-table'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import Select from '@isoftdata/svelte-select'
	import Button from '@isoftdata/svelte-button'
	import PartSearchModal from 'components/PartSearchModal.svelte'
	import Modal from '@isoftdata/svelte-modal'
	import PartInfo from './Basic.svelte'
	import { Attachments } from '@isoftdata/svelte-attachments'

	import { graphql } from '$houdini'
	import { getContext, onMount, tick } from 'svelte'
	import { format as formatCurrency } from '@isoftdata/utility-currency'
	import { v4 as uuid } from '@lukeed/uuid'
	import { loadPart, getOptionValueMap, computeTypeFields, newPart } from 'utility/load-part'
	import { klona } from 'klona'
	import { getBootstrapBreakpoint } from '@isoftdata/utility-bootstrap'
	import { getEventValue, getEventChecked, getEventValueEnum } from '@isoftdata/browser-event'
	import { stringToBoolean } from '@isoftdata/utility-string'
	import userLocalWritable from 'stores/user-local-writable'
	import session from 'stores/session'
	import Card from '@isoftdata/svelte-card'
	import Input from '@isoftdata/svelte-input'
	import { dequal } from 'dequal'
	import Icon from '@isoftdata/svelte-icon'

	interface Props {
		part: Part
		asr: SvelteAsr
		settingValues: SettingValues
		userSettings: UserSettings
		// part-basic stuff
		assyModels: Array<PartModel>
		glCategories: PartScreenStageOne$result['glCategories']
		inventoryConditions: PartScreenStageOne$result['inventoryConditions']
		inventoryTypeList: PartScreenStageOne$result['inventoryTypeList']
		shouldDisable: GetFieldEditabilityFn
		/** "True" Models*/
		partModels: Array<PartModel>
		partStoreLocations: LIB_partStoreData$result['locations']
		saleClasses: PartScreenStageOne$result['saleClasses']
		sellPriceClasses: PartScreenStageOne$result['sellPriceClasses']
		userPartStatusList: PartScreenStageOne$result['userPartStatusList']
		vehicleMakes: PartScreenStageOne$result['vehicleMakes']
		vehicleModels: Array<string>
		vendorList: PartScreenStageOne$result['vendors']['items']
	}

	type MembershipRow = CollectionMembershipLocalInventory$data & { parentPartId: number | null }
	type MembershipNode = TreeNode<MembershipRow, 'innodbInventoryid', 'parentPartId'>

	type CollectionMemberInfo = {
		id?: number
		skuNumber?: number
		costFactor: string
		quantity: string
		integral: boolean
	}

	let {
		part = $bindable(),
		asr,
		settingValues,
		userSettings,
		assyModels,
		glCategories,
		inventoryConditions,
		inventoryTypeList,
		shouldDisable,
		partModels,
		partStoreLocations,
		saleClasses,
		sellPriceClasses,
		userPartStatusList,
		vehicleMakes,
		vehicleModels,
		vendorList,
	}: Props = $props()

	/*
		Since updating directly to $prop.part will trigger a change and enable the save button, and we only grab all the collection data in this state which will trigger a change to $prop.part,
		we need to make a local copy of the collection data to work with and then only update $prop.part.collection when we make changes to it.
		This is not ideal as we now have two sources of the collection data, and needed to update $prop.part "manually", but it is the best I can do without a major refactor.
	*/
	let partCollection: Part['collection'] = $state(klona(part.collection) ?? null)

	let membersTable: Table<CollectionMember> | undefined = $state(undefined)
	let membershipTable: Table<MembershipNode> | undefined = $state(undefined)
	let breakdownTemplates: BreakdownTemplates$result['inventoryType']['breakdownTemplates'] = $state([])
	let selectedMemberIndex: number = $state(-1)
	let loadingMemberIndex: number | null = $state(null)
	let partSearchModal: PartSearchModal | undefined = $state(undefined)
	let breakdownTemplate: BreakdownTemplates$result['inventoryType']['breakdownTemplates'][number] | null = $state(null)
	let showPartModal = $state(false)
	let breakpoint = $state(getBootstrapBreakpoint())
	let sortedRows: Array<CollectionMember & IndexedRowProps> = $state([])
	let showAttachmentModal = $state(false)
	let collectionMembershipTree = $state(makeCollectionMembershipTree())

	const { t: translate } = getContext<i18n>('i18next')
	const collectionCrudStore = getContext<CrudStore<CollectionMember, 'uuid'>>('collectionCrudStore')

	const viewMode = userLocalWritable($session.id, 'partLinksViewMode', 'LIST')

	// #region Constants
	const membersColumns: Array<Column<CollectionMember>> = [
		{
			name: translate('common:status', 'Status'),
			property: 'part[status]',
			width: '1rem',
			footer: {
				fn: (count, status) => {
					if (status === 'A' && typeof count === 'number') {
						count++
					}
					return count
				},
				initialValue: 0,
			},
		},
		{
			name: translate('common:tagNum', 'Tag #'),
			property: 'part[tagNumber]',
		},
		{
			name: translate('common:typeNum', 'Type #'),
			property: 'part[inventoryType][id]',
			numeric: true,
		},
		{
			name: translate('common:partType', 'Part Type'),
			property: 'part[inventoryType][name]',
		},
		{
			name: '',
			icon: 'paperclip',
			property: 'part[attachmentCount]',
			width: '1rem',
			align: 'center',
			footer: {
				fn: 'SUM',
			},
		},
		{
			name: translate('part.costFactor', 'Cost Factor'),
			property: 'costFactor',
		},
		{
			name: translate('part.integral', 'Integral'),
			property: 'integral',
		},
		{
			name: translate('part.assyManufacturer', 'Assy. Manufacturer'),
			property: 'part[parentManufacturer][name]',
		},
		{
			name: translate('part.assyModel', 'Assy. Model'),
			property: 'part[parentModel][name]',
		},

		{
			name: translate('common:manufacturer', 'Manufacturer'),
			property: 'part[manufacturer][name]',
		},
		{
			name: translate('common:model', 'Model'),
			property: 'part[model][name]',
		},
		{
			name: translate('common:category', 'Category'),
			property: 'part[category][name]',
		},
		{
			name: translate('common:side', 'Side'),
			property: 'part[side]',
		},
		{
			name: translate('common:cost', 'Cost'),
			property: 'part[cost]',
			numeric: true,
			footer: {
				fn: 'SUM',
				formatCurrency: true,
			},
		},
		{
			name: translate('common:retail', 'Retail'),
			property: 'part[retailPrice]',
			numeric: true,
			footer: {
				fn: 'SUM',
				formatCurrency: true,
			},
		},
		{
			name: translate('common:wholesale', 'Wholesale'),
			property: 'part[wholesalePrice]',
			numeric: true,
			footer: {
				fn: 'SUM',
				formatCurrency: true,
			},
		},
		{
			name: translate('part.jobber', 'Jobber'),
			property: 'part[jobberPrice]',
			numeric: true,
			footer: {
				fn: 'SUM',
				formatCurrency: true,
			},
		},
		{
			name: translate('part.retailCore', 'Retail Core'),
			property: 'part[retailCorePrice]',
			numeric: true,
			footer: {
				fn: 'SUM',
				formatCurrency: true,
			},
		},
	] as const
	// Desktop only displays "one level" of collections, IE, this part and its parent collection, even if that collection is in another collection
	const collectionsColumns: Array<Column<MembershipNode>> = [
		{
			name: translate('common:tagNum', 'Tag #'),
			property: 'tagNumber',
		},
		{
			name: '',
			property: 'innodbInventoryid',
			title: translate('part.viewPart', 'View Part'),
			width: '1rem',
			icon: 'search',
			sortType: false,
			align: 'center',
		},
		{
			name: translate('common:partType', 'Part Type'),
			property: 'inventoryType[id]',
		},
		{
			name: translate('common:quantity', 'Quantity'),
			property: 'quantity',
		},
		{
			name: translate('part.qoh', 'QOH'),
			property: 'quantityAvailable',
		},
		{
			name: translate('common:manufacturer', 'Manufacturer'),
			property: 'manufacturer[name]',
		},
		{
			name: translate('common:model', 'Model'),
			property: 'model[name]',
		},
		{
			name: translate('common:category', 'Category'),
			property: 'category[name]',
		},
	]
	// #endregion

	// #region Queries
	const breakdownTemplatesQuery = graphql(`
		query BreakdownTemplates($inventoryTypeId: Int!) {
			inventoryType(id: $inventoryTypeId) {
				breakdownTemplates {
					components {
						integral
						inventoryTypeId
						quantity
						rank
					}
					default
					id
					name
				}
			}
		}
	`)
	// #endregion

	function makeCollectionMembershipTree(): Array<MembershipNode> {
		if (!part.collectionMembership.length) {
			return []
		}

		const parentPart = part.collectionMembership[0].collection.inventory
			? {
					...part.collectionMembership[0].collection.inventory,
					parentPartId: null,
				}
			: null

		const siblings = part.collectionMembership[0].collection.members.reduce((acc: Array<MembershipRow>, member) => {
			if (member?.localInventory) {
				acc.push({
					...member.localInventory,
					parentPartId: parentPart?.innodbInventoryid ?? null,
				})
			}
			return acc
		}, [])

		// Parent part might not be at the current store, so fall back to just rendering the siblings in that case?
		const rowsToTreeify = parentPart ? [parentPart, ...siblings] : siblings

		return treeify<MembershipRow, 'innodbInventoryid', 'parentPartId'>(
			rowsToTreeify,
			'innodbInventoryid',
			'parentPartId',
		)
	}

	function collectionMemberHasChanged(member: CollectionMember): boolean {
		if (!member.loaded || member.template) {
			return false
		}
		// Will also catch changes to Q&A
		const crudHasChanges =
			collectionCrudStore.isCreated(member) ||
			collectionCrudStore.isUpdated(member) ||
			collectionCrudStore.isDeleted(member)
		if (crudHasChanges) {
			return true
		}
		const partHasChanges = !dequal(member.part, member.origPart)
		if (partHasChanges) {
			collectionCrudStore.update(member)
			part.collection = partCollection
			return true
		}

		const attachmentsHaveChanges = member.attachments.some(attachment => !!attachment.action)
		if (attachmentsHaveChanges) {
			collectionCrudStore.update(member)
			part.collection = partCollection
			return true
		}
		return false
	}

	function newCollectionMember(): void {
		if (!partCollection) {
			return
		}
		const newMember = newPart(part.storeId, settingValues, userSettings)

		// get default location from parent part
		if (part.locations.length) {
			newMember.part.locations = [
				{
					...klona(part.locations[0]),
					uuid: uuid(),
				},
			]
		}

		partCollection.members.push(makeCollectionMember(newMember.part, newMember.attachments))
		partCollection.members = partCollection.members
		selectedMemberIndex = partCollection.members.length - 1
		if (isMobile || $viewMode === 'TABLE') {
			showPartModal = true
		}
		collectionCrudStore.create(partCollection.members[selectedMemberIndex])
		part.collection = partCollection
	}

	async function loadCollectionMember(
		partial: { skuNumber: number; storeId: number; uuid?: string },
		memberInfo?: CollectionMemberInfo,
	): Promise<LoadedCollectionMember> {
		const { part, attachments } = await loadPart({
			sku: partial.skuNumber,
			useCachedPart: false,
			settingValues,
			userSettings,
			storeId: partial.storeId,
		})

		part.glCategory = glCategories.find(cat => cat.id === part.glCategory?.id) ?? null
		part.saleClass = saleClasses.find(saleClass => saleClass.code === part.saleClass?.code) ?? part.saleClass

		return makeCollectionMember(part, attachments, memberInfo)
	}

	function makeCollectionMember(
		part: Part,
		attachments: Array<FlatAttachment>,
		collectionMemberInfo: CollectionMemberInfo = { costFactor: '0', quantity: '1', integral: false },
		template: boolean | undefined = undefined,
	): LoadedCollectionMember {
		return {
			...collectionMemberInfo,
			part,
			attachments,
			loaded: true,
			origPart: klona(part),
			optionValueMap: getOptionValueMap(part),
			typeFields: computeTypeFields(part, settingValues),
			template,
			uuid: uuid(),
		}
	}

	// function collectionMemberIsLoaded(member: CollectionMember): member is LoadedCollectionMember {
	// 	return member.loaded
	// }

	function getNextOrPrevMemberIndex(
		rows: Array<CollectionMember & IndexedRowProps>,
		selectedMemberIndex: number | null,
		offset: 1 | -1,
	) {
		const currentSortedIndex = rows.findIndex(row => row.originalIndex === selectedMemberIndex)
		const nextIndex = currentSortedIndex + offset
		if (nextIndex >= 0 && nextIndex < rows.length) {
			return rows[nextIndex].originalIndex
		}
		return null
	}

	async function collectionMemberSelected(index: number, doNotOpen = false): Promise<void> {
		if (collectionType !== 'ASSEMBLY' || index === loadingMemberIndex || !partCollection) {
			return
		}

		const member = partCollection.members[index]

		if (!member.loaded && member.part.innodbInventoryid) {
			loadingMemberIndex = index

			partCollection.members[index] = await loadCollectionMember(member.part, member)
			await tick()
			loadingMemberIndex = null
		}
		if (!doNotOpen && (isMobile || $viewMode === 'TABLE')) {
			showPartModal = true
		}
		selectedMemberIndex = index
	}

	function onTemplateChange(): void {
		if (!partCollection) {
			return
		}
		// remove existing template members
		partCollection.members = partCollection.members.filter(member => !member.template)
		if (breakdownTemplate) {
			// add new template members
			const templateParts = (breakdownTemplate.components ?? []).reduce(
				(acc: Array<LoadedCollectionMember>, component) => {
					const foundType = inventoryTypeList.find(it => it.inventoryTypeId === component.inventoryTypeId) ?? null
					// if the inventory type isn't in the array, it is not active, so don't add the part
					if (foundType) {
						const newComponent = newPart(part.storeId, settingValues, userSettings)
						newComponent.part.inventoryTypeId = component.inventoryTypeId ?? null
						newComponent.part.inventoryType = foundType
							? {
									id: foundType.inventoryTypeId,
									name: foundType.name,
									setId: foundType.typeSetId,
									typeData1History: [],
									typeData2History: [],
									typeData3History: [],
									typeData4History: [],
								}
							: null
						acc.push(makeCollectionMember(newComponent.part, newComponent.attachments, undefined, true))
					}

					return acc
				},
				[],
			)

			partCollection.members.push(...templateParts)
			partCollection.members = partCollection.members
		}
	}

	function removeCollectionItem(index: number): void {
		if (!partCollection) {
			return
		}

		if (partCollection.members[index].template === false) {
			// if we remove a template item, just set it back as a template item
			partCollection.members[index].template = true
			collectionCrudStore.delete(partCollection.members[index])
			part.collection = partCollection
		} else if (selectedMemberIndex > -1) {
			collectionCrudStore.delete(partCollection.members[index])
			partCollection.members.splice(index, 1)
			partCollection.members = partCollection.members
			if (selectedMemberIndex === index) {
				selectedMemberIndex = partCollection.members.length ? 0 : -1
			}
			part.collection = partCollection
		}
	}

	function updateCollectionItem(index: number | null) {
		if (index !== null && partCollection?.members[index]) {
			collectionCrudStore.update(partCollection.members[index])
			part.collection = partCollection
		}
	}

	let collectionType = $derived(partCollection?.type ?? null)
	let isCollection = $derived(!!partCollection)
	let selectedMember = $derived(
		selectedMemberIndex > -1 ? (partCollection?.members[selectedMemberIndex] ?? null) : null,
	)
	let isMobile = $derived(breakpoint === 'xs' || breakpoint === 'sm')
	let showSplitView = $derived(!!selectedMember && !isMobile && $viewMode === 'LIST')
	let nextIndex = $derived(getNextOrPrevMemberIndex(sortedRows, selectedMemberIndex, 1))
	let prevIndex = $derived(getNextOrPrevMemberIndex(sortedRows, selectedMemberIndex, -1))

	$effect(() => {
		membersTable?.setColumnVisibility(['costFactor', 'integral'], collectionType === 'ASSEMBLY')
	})
	$effect(() => {
		membersTable?.setColumnVisibility(
			[
				'part[side]',
				'part[cost]',
				'part[retailPrice]',
				'part[wholesalePrice]',
				'part[jobberPrice]',
				'part[retailCorePrice]',
			],
			collectionType === 'ASSEMBLY' && $viewMode === 'TABLE',
		)
	})
	$effect(() => {
		membersTable?.setColumnVisibility(
			[
				'costFactor',
				'integral',
				'part[parentManufacturer][name]',
				'part[parentModel][name]',
				'part[manufacturer][name]',
				'part[model][name]',
				'part[category][name]',
			],
			$viewMode === 'TABLE',
		)
	})
	// Really terrible change detection since the PartInfo component wasn't built to do this
	$effect(() => {
		partCollection?.members.forEach(member => collectionMemberHasChanged(member))
	})

	// $: membersTable?.setColumnVisibility(['part[attachmentCount]'], $viewMode === 'LIST')

	onMount(async () => {
		if (collectionMembershipTree.length && membershipTable) {
			membershipTable.expandRow(collectionMembershipTree[0].innodbInventoryid, true)
		}
		if (part.collection?.members.length) {
			collectionMemberSelected(0, true)
		}
		if (part.inventoryType) {
			const { data } = await breakdownTemplatesQuery.fetch({
				variables: {
					inventoryTypeId: part.inventoryType?.id,
				},
			})
			breakdownTemplates = data?.inventoryType?.breakdownTemplates ?? []
		}
	})
</script>

<svelte:window onresize={() => (breakpoint = getBootstrapBreakpoint())} />

<div class="row">
	<div
		class="col-12"
		class:col-lg-4={selectedMemberIndex !== null && showSplitView}
	>
		<div style="position: sticky; top: 0.5rem;">
			<Card
				headerClass="h4"
				footerClass="d-flex justify-content-between flex-wrap"
				header={translate('part.collectionInformation', 'Collection Information')}
			>
				<Table
					responsive
					showFooter
					filterEnabled
					columnHidingEnabled
					idProp="uuid"
					rowSelectionIdProp="uuid"
					headerColumnClass="col"
					filterColumnClass="{showSplitView ? '' : 'col-md-4 col-lg-2'} col-12 align-self-end"
					parentStyle="overflow-y: auto; max-height: 50vh;"
					columns={membersColumns}
					rows={partCollection?.members ?? []}
					bind:sortedRows
					bind:this={membersTable}
				>
					{#snippet header()}
						<div class="row mb-3">
							<div
								class="col-auto align-content-end me-auto me-md-0"
								class:flex-grow-1={showSplitView}
							>
								<Checkbox
									label={translate('part.thisPartIsACollection', 'This Part is a Collection')}
									checked={isCollection}
									onchange={event => {
										if (getEventChecked(event)) {
											partCollection = {
												type: 'ASSEMBLY',
												members: [],
											}
										} else {
											partCollection = null
										}
										part.collection = partCollection
									}}
								></Checkbox>
							</div>

							<div
								class="col-auto align-content-end"
								class:col-2={showSplitView}
								class:order-md-last={!showSplitView}
							>
								<div>
									<!-- TODO: RadioButtonGroup? -->
									<Checkbox
										showLabel={false}
										type="radio"
										trueLabel=""
										trueIcon="table"
										falseLabel=""
										falseIcon="list"
										checked={$viewMode === 'TABLE'}
										onchange={event => {
											$viewMode = stringToBoolean(getEventValue(event)) ? 'TABLE' : 'LIST'
											if ($viewMode === 'TABLE') {
												selectedMemberIndex = -1
											}
										}}
									></Checkbox>
								</div>
							</div>
							<div
								class:col-6={showSplitView && collectionType === 'ASSEMBLY'}
								class:col-12={!showSplitView || collectionType !== 'ASSEMBLY'}
								class:col-md-4={!showSplitView}
								class:col-lg-2={!showSplitView}
							>
								<Select
									labelParentClass="mb-0"
									label={translate('part.collectionType', 'Collection Type')}
									required={isCollection}
									disabled={!isCollection}
									value={partCollection?.type ?? null}
									onchange={event => {
										if (!partCollection) {
											return
										}
										partCollection.type = getEventValueEnum(event, 'ASSEMBLY', 'KIT', 'ORDERED_KIT', 'TEMPLATE')
										if (partCollection.type !== 'ASSEMBLY') {
											breakdownTemplate = null
										}
									}}
								>
									<option value="ASSEMBLY">{translate('common:assembly', 'Assembly')}</option>
									<option value="KIT">{translate('part.kit', 'Kit')}</option>
									<option value="ORDERED_KIT">{translate('part.orderedKit', 'Ordered Kit')}</option>
									<option value="TEMPLATE">{translate('common:template', 'Template')}</option>
								</Select>
							</div>
							{#if collectionType === 'ASSEMBLY'}
								<div
									class="me-md-auto"
									class:col-6={showSplitView}
									class:col-12={!showSplitView}
									class:col-md-4={!showSplitView}
									class:col-lg-2={!showSplitView}
								>
									<Select
										labelParentClass="mb-0"
										label={translate('common:template', 'Template')}
										disabled={collectionType !== 'ASSEMBLY'}
										options={breakdownTemplates}
										bind:value={breakdownTemplate}
										onchange={onTemplateChange}
									>
										{#snippet option({ option })}
											<option value={option}>{option.name}</option>
										{/snippet}
									</Select>
								</div>
							{/if}
						</div>
					{/snippet}

					{#snippet body({ rows, visibleColumnsCount })}
						{#if rows.length}
							{#each rows as { part: rowPart, originalIndex, template, ...row } (row.uuid)}
								<tr
									class="text-nowrap cursor-pointer"
									class:table-primary={selectedMemberIndex === originalIndex || loadingMemberIndex === originalIndex}
									onclick={() => collectionMemberSelected(originalIndex)}
								>
									<Td property="part[status]">
										<span class="me-2">{rowPart.status}</span>
										{#if template}
											<Button
												outline
												size="sm"
												color="success"
												iconClass="plus"
												onclick={() => {
													if (!partCollection) {
														return
													}

													partCollection.members[originalIndex].template = false
													selectedMemberIndex = originalIndex
													collectionCrudStore.create(partCollection.members[originalIndex])
													part.collection = partCollection
												}}
											></Button>
										{:else if !rowPart.innodbInventoryid}
											<!-- Remove template or other item -->
											<Button
												outline
												size="sm"
												color="danger"
												iconClass="trash-undo"
												onclick={() => removeCollectionItem(originalIndex)}
											></Button>
										{/if}
										{#if loadingMemberIndex === originalIndex}
											<Icon
												fixedWidth
												isLoading
											></Icon>
										{/if}
									</Td>
									<Td
										stopPropagation
										property="part[tagNumber]"
									>
										{#if rowPart.innodbInventoryid && rowPart.tagNumber}
											<a
												href={asr.makePath('app.part', {
													sku: rowPart.skuNumber,
													storeId: rowPart.storeId,
													tab: 'links',
												})}>{rowPart.tagNumber}</a
											>
										{:else if rowPart.tagNumber}
											{rowPart.tagNumber}
										{:else}
											<i class="text-body-secondary">{translate('common:notSlashAcceptable', 'N/A')}</i>
										{/if}
									</Td>
									<Td property="part[inventoryType][id]">
										{#if rowPart.inventoryType}
											{rowPart.inventoryType.id}
										{/if}
									</Td>
									<Td property="part[inventoryType][name]">
										{rowPart.inventoryType?.name ?? ''}
									</Td>
									<Td
										stopPropagation
										property="part[attachmentCount]"
									>
										<Button
											outline
											size="sm"
											iconClass="paperclip"
											onclick={async () => {
												await collectionMemberSelected(originalIndex, true)
												showAttachmentModal = true
											}}>{rowPart.attachmentCount}</Button
										>
									</Td>
									<Td property="costFactor">
										{row.costFactor ?? ''}
									</Td>
									<Td property="integral">
										{row.integral ? translate('common:yes', 'Yes') : translate('common:no', 'No')}
									</Td>
									<Td property="part[parentManufacturer][name]">
										{rowPart.parentManufacturer?.name ?? ''}
									</Td>
									<Td property="part[parentModel][name]">
										{rowPart.parentModel?.name ?? ''}
									</Td>
									<Td property="part[manufacturer][name]">
										{rowPart.manufacturer?.name ?? ''}
									</Td>
									<Td property="part[model][name]">
										{rowPart.model?.name ?? ''}
									</Td>
									<Td property="part[category][name]">
										{rowPart.category?.name ?? ''}
									</Td>
									<Td property="part[side]">
										{rowPart.side}
									</Td>
									<Td property="part[cost]">
										{formatCurrency(rowPart.cost)}
									</Td>
									<Td property="part[retailPrice]">
										{formatCurrency(rowPart.retailPrice)}
									</Td>
									<Td property="part[wholesalePrice]">
										{formatCurrency(rowPart.wholesalePrice)}
									</Td>
									<Td property="part[jobberPrice]">
										{formatCurrency(rowPart.jobberPrice)}
									</Td>
									<Td property="part[retailCorePrice]">
										{formatCurrency(rowPart.retailCorePrice)}
									</Td>
								</tr>
							{/each}
						{:else}
							<tr>
								<td
									class="text-center"
									colspan={visibleColumnsCount}
									>{#if isCollection}{translate(
											'part.thisCollectionHasNoMembers',
											'This collection has no members',
										)}{:else}{translate('part.thisPartIsNotACollection', 'This part is not a collection')}{/if}</td
								>
							</tr>
						{/if}
					{/snippet}
				</Table>
				{#snippet footer()}
					<div>
						{#if collectionType === 'ASSEMBLY'}
							<Button
								outline
								size="sm"
								color="success"
								iconClass="plus"
								title={translate('part.addANewPartToThisCollection', 'Add a new part to this collection')}
								onclick={newCollectionMember}
								>{translate('part.newItem', 'New Item')}
							</Button>
						{/if}
						<Button
							outline
							size="sm"
							color="success"
							icon={{
								prefix: 'far',
								icon: 'magnifying-glass-plus',
							}}
							title={translate(
								'part.searchForAPartToAddToThisCollection',
								'Search for a part to add to this collection',
							)}
							onclick={() => partSearchModal?.show()}
							>{translate('part.addExistingPart', 'Add Existing Part')}
						</Button>
						<Button
							outline
							size="sm"
							color="danger"
							iconClass="xmark"
							title={translate('part.removeThisItemFromThisCollection', 'Remove this item from this collection')}
							disabled={selectedMemberIndex === null}
							onclick={() => selectedMemberIndex !== null && removeCollectionItem(selectedMemberIndex)}
							>{translate('part.remove', 'Remove')}
						</Button>
					</div>
					<div>
						<Button
							outline
							size="sm"
							iconClass="search"
							title={translate('part.searchForPartsInThisCollection', 'Search for parts in this collection')}
							href={asr.makePath(
								'app.part-search.results',
								{
									ADDITIONAL_FIELDS: JSON.stringify({
										'collectionparent.tagnumber': part.tagNumber,
									}),
								},
								{ inherit: false },
							)}
							>{translate('part.members', 'Members')}
						</Button>
					</div>
				{/snippet}
			</Card>
			<Card
				class="mt-2"
				headerClass="h4"
				header={translate('part.collectionsContainingThisPart', 'Collections Containing This Part')}
			>
				<Table
					tree
					responsive
					columnHidingEnabled
					columns={collectionsColumns}
					rows={collectionMembershipTree}
					bind:this={membershipTable}
				>
					{#snippet children({ row })}
						<TreeRow
							class="text-nowrap"
							property="tagNumber"
							idProp="innodbInventoryid"
							parentIdProp="parentPartId"
							node={row}
						>
							{#snippet children({ node })}
								<Td property="innodbInventoryid">
									<Button
										outline
										size="xs"
										class="ms-auto"
										iconClass="search"
										title={translate('part.viewThisPart', 'View this part')}
										disabled={node.innodbInventoryid === part.innodbInventoryid}
										href={asr.makePath('app.part', { sku: node.skuNumber, storeId: node.storeId })}
									></Button>
								</Td>
								<Td property="inventoryType[id]">
									{node.inventoryType?.id} - {node.inventoryType?.name}
								</Td>
								<Td property="quantity">
									{node.quantity}
								</Td>
								<Td property="quantityAvailable">
									{node.quantityAvailable}
								</Td>
								<Td property="manufacturer[name]">
									{node.manufacturer?.name ?? ''}
								</Td>
								<Td property="model[name]">
									{node.model?.name ?? ''}
								</Td>
								<Td property="category[name]">
									{node.category?.name ?? ''}
								</Td>
							{/snippet}
						</TreeRow>
					{/snippet}
					{#snippet noRows({ visibleColumnsCount })}
						<tr>
							<td
								class="text-center"
								colspan={visibleColumnsCount}
								>{translate(
									'part.thisPartIsNotAMemberOfAnyCollections',
									'This part is not a member of any collections',
								)}</td
							>
						</tr>
					{/snippet}
				</Table>
			</Card>
		</div>
	</div>
	{#if selectedMemberIndex !== null && selectedMember?.loaded && partCollection && partCollection.members[selectedMemberIndex].loaded && showSplitView}
		<div class="col-12 col-md-8">
			<div class="d-flex justify-content-between mb-2">
				<div>
					{#if !selectedMember.part.innodbInventoryid && !selectedMember.template}
						<Button
							outline
							color="danger"
							iconClass="trash-undo"
							onclick={() => {
								// Template item
								if (selectedMemberIndex !== null) {
									removeCollectionItem(selectedMemberIndex)
								}
							}}
							>{translate('part.undoNewPart', 'Undo New Part')}
						</Button>
					{/if}
					<!-- TODO File input? attachment component? Image viewer? -->
					<Button
						outline
						color="secondary"
						iconClass="camera"
						disabled={selectedMember.template}
						onclick={async () => {
							showAttachmentModal = true
							await tick()
							document.getElementById('attachment-upload')?.click()
						}}
					></Button>
					<Button
						outline
						iconClass="paperclip"
						onclick={() => {
							showAttachmentModal = true
						}}>{selectedMember.part.attachmentCount}</Button
					>
				</div>
				{#if selectedMember.template}
					<div class="text-center align-content-end">
						<h6 class="text-danger mb-0">{translate('common:template', 'Template')}</h6>
					</div>
				{/if}
				<div>
					<Button
						outline
						color="secondary"
						iconClass="caret-left"
						disabled={prevIndex === null}
						onclick={() => prevIndex !== null && collectionMemberSelected(prevIndex)}
						>{translate('common:previousABBR', 'Prev')}
					</Button>
					<Button
						outline
						color="secondary"
						iconClass="caret-right"
						disabled={nextIndex === null}
						onclick={() => nextIndex !== null && collectionMemberSelected(nextIndex)}
						>{translate('common:next', 'Next')}
					</Button>
				</div>
			</div>

			<!-- API does not and apparently will not support updating the inventory object for saved collection members, so don't let them edit saved collection members -->
			<PartInfo
				{asr}
				{assyModels}
				{glCategories}
				{inventoryConditions}
				{inventoryTypeList}
				{partStoreLocations}
				{saleClasses}
				{sellPriceClasses}
				{settingValues}
				{userPartStatusList}
				{vehicleMakes}
				{vehicleModels}
				{vendorList}
				{partModels}
				{shouldDisable}
				disabled={selectedMember.template || collectionType !== 'ASSEMBLY'}
				inventoryTypeData={inventoryTypeList.find(it => it.inventoryTypeId === selectedMember.part.inventoryType?.id) ??
					null}
				bind:part={selectedMember.part}
				bind:optionValueMap={selectedMember.optionValueMap}
				bind:origPart={selectedMember.origPart}
				bind:typeFields={selectedMember.typeFields}
				qaChange={() => updateCollectionItem(selectedMemberIndex)}
			/>
			<Card
				collapsible
				entireHeaderToggles
				class="mt-2"
				headerClass="card-header d-flex justify-content-between h4"
				header={translate('part.collectionMemberInfo', 'Collection Member Info')}
			>
				<div class="row">
					<div class="col-4">
						<Input
							type="number"
							label={translate('common:quantity', 'Quantity')}
							bind:value={partCollection.members[selectedMemberIndex].quantity}
							onchange={() => updateCollectionItem(selectedMemberIndex)}
						></Input>
					</div>
					<div class="col-4">
						<Input
							type="number"
							label={translate('part.costFactor', 'Cost Factor')}
							bind:value={partCollection.members[selectedMemberIndex].costFactor}
							onchange={() => updateCollectionItem(selectedMemberIndex)}
						></Input>
					</div>
					<div class="col-4 align-content-end">
						<Checkbox
							label={translate('part.integral', 'Integral')}
							bind:checked={partCollection.members[selectedMemberIndex].integral}
							onchange={() => updateCollectionItem(selectedMemberIndex)}
						></Checkbox>
					</div>
				</div>
			</Card>
		</div>
	{/if}
</div>

<PartSearchModal
	bind:this={partSearchModal}
	chooseItem={async item => {
		if (!partCollection) {
			return
		}

		const newMember = await loadCollectionMember({
			skuNumber: item.skuNumber,
			storeId: item.store.id,
		})
		partCollection.members.push(newMember)
		partCollection.members = partCollection.members
		selectedMemberIndex = partCollection.members.length - 1
		collectionCrudStore.create(partCollection.members[selectedMemberIndex])
		part.collection = partCollection
	}}
></PartSearchModal>

<Modal
	confirmShown={false}
	modalSize="xxl"
	title={translate('partSearch.editPart', 'Edit Part')}
	bind:show={showPartModal}
	close={() => {
		showPartModal = false
		if (partCollection) {
			partCollection.members = partCollection.members
		}
	}}
>
	<!-- Ignore the type errors, they've become endemic and I can't get rid of them -->
	{#if selectedMemberIndex !== null && selectedMember?.loaded && partCollection}
		<PartInfo
			{asr}
			{assyModels}
			{glCategories}
			{inventoryConditions}
			{inventoryTypeList}
			{partStoreLocations}
			{saleClasses}
			{sellPriceClasses}
			{settingValues}
			{userPartStatusList}
			{vehicleMakes}
			{vehicleModels}
			{vendorList}
			{partModels}
			{shouldDisable}
			disabled={selectedMember.template || collectionType !== 'ASSEMBLY'}
			inventoryTypeData={inventoryTypeList.find(it => it.inventoryTypeId === selectedMember.part.inventoryType?.id) ??
				null}
			bind:part={selectedMember.part}
			bind:optionValueMap={selectedMember.optionValueMap}
			bind:origPart={selectedMember.origPart}
			bind:typeFields={selectedMember.typeFields}
			qaChange={() => updateCollectionItem(selectedMemberIndex)}
		/>
		<Card
			collapsible
			entireHeaderToggles
			class="mt-2"
			headerClass="card-header d-flex justify-content-between h4"
			header={translate('part.collectionMemberInfo', 'Collection Member Info')}
		>
			<div class="row">
				<div class="col-4">
					<Input
						type="number"
						label={translate('common:quantity', 'Quantity')}
						bind:value={partCollection.members[selectedMemberIndex].quantity}
						onchange={() => updateCollectionItem(selectedMemberIndex)}
					></Input>
				</div>
				<div class="col-4">
					<Input
						type="number"
						label={translate('part.costFactor', 'Cost Factor')}
						bind:value={partCollection.members[selectedMemberIndex].costFactor}
						onchange={() => updateCollectionItem(selectedMemberIndex)}
					></Input>
				</div>
				<div class="col-4 align-content-end">
					<Checkbox
						label={translate('part.integral', 'Integral')}
						bind:checked={partCollection.members[selectedMemberIndex].integral}
						onchange={() => updateCollectionItem(selectedMemberIndex)}
					></Checkbox>
				</div>
			</div>
		</Card>
	{/if}
</Modal>

<Modal
	confirmShown={false}
	backdropClickCancels={false}
	modalSize="xxl"
	title={translate('common:attachments', 'Attachments')}
	bind:show={showAttachmentModal}
	close={() => (showAttachmentModal = false)}
>
	{#if partCollection && selectedMemberIndex !== null}
		<Attachments
			bind:fileList={partCollection.members[selectedMemberIndex].attachments}
			filesAdded={files => {
				if (partCollection && selectedMemberIndex !== null) {
					partCollection.members[selectedMemberIndex].part.attachmentCount += files.length
					updateCollectionItem(selectedMemberIndex)
				}
			}}
			filesDeleted={files => {
				if (partCollection && selectedMemberIndex !== null) {
					partCollection.members[selectedMemberIndex].part.attachmentCount -= files.length
					updateCollectionItem(selectedMemberIndex)
				}
			}}
			publicChange={() => {
				updateCollectionItem(selectedMemberIndex)
			}}
			rankChange={() => {
				updateCollectionItem(selectedMemberIndex)
			}}
		></Attachments>
	{:else}
		<p>{translate('part.noPartSelected', 'No part selected')}</p>
	{/if}
</Modal>
